import React from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationItem } from './NotificationItem';
import type { NotificationCategory } from '../../types/notifications';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onViewAll: () => void;
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  onViewAll,
}) => {
  const { notifications, unreadCount, criticalCount, markAllAsRead, filterByCategory, filterCritical } = useNotifications();

  const [activeFilter, setActiveFilter] = React.useState<'all' | 'critical' | NotificationCategory>('all');

  const filteredNotifications = React.useMemo(() => {
    if (activeFilter === 'all') {
      return notifications.slice(0, 5);
    }
    if (activeFilter === 'critical') {
      return filterCritical().slice(0, 5);
    }
    return filterByCategory(activeFilter as NotificationCategory).slice(0, 5);
  }, [notifications, activeFilter, filterByCategory, filterCritical]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dropdown */}
      <div 
        className="absolute right-0 top-full mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden"
        role="dialog"
        aria-label="Notifications"
      >
        {/* Header */}
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <span className="text-xs text-gray-500">
                  {unreadCount} unread
                </span>
              )}
              {criticalCount > 0 && (
                <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  {criticalCount} critical
                </span>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-1 mt-2 -mx-1 overflow-x-auto">
            <FilterButton 
              active={activeFilter === 'all'} 
              onClick={() => setActiveFilter('all')}
            >
              All
            </FilterButton>
            <FilterButton 
              active={activeFilter === 'critical'} 
              onClick={() => setActiveFilter('critical')}
              variant="critical"
            >
              Critical
            </FilterButton>
            <FilterButton 
              active={activeFilter === 'Evidence'} 
              onClick={() => setActiveFilter('Evidence')}
            >
              Evidence
            </FilterButton>
            <FilterButton 
              active={activeFilter === 'Case'} 
              onClick={() => setActiveFilter('Case')}
            >
              Cases
            </FilterButton>
            <FilterButton 
              active={activeFilter === 'LegalHold'} 
              onClick={() => setActiveFilter('LegalHold')}
            >
              Legal Hold
            </FilterButton>
          </div>
        </div>

        {/* Notification List */}
        <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
          {filteredNotifications.length === 0 ? (
            <div className="py-8 text-center">
              <svg 
                className="w-12 h-12 mx-auto text-gray-300" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={1.5} 
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" 
                />
              </svg>
              <p className="mt-2 text-sm text-gray-500">No notifications</p>
            </div>
          ) : (
            filteredNotifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                compact
                onActionClick={onClose}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsRead()}
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              Mark all as read
            </button>
          )}
          <button
            onClick={onViewAll}
            className="text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors ml-auto"
          >
            View all notifications →
          </button>
        </div>
      </div>
    </>
  );
};

interface FilterButtonProps {
  active: boolean;
  onClick: () => void;
  variant?: 'default' | 'critical';
  children: React.ReactNode;
}

const FilterButton: React.FC<FilterButtonProps> = ({ active, onClick, variant = 'default', children }) => {
  const baseClasses = 'px-2 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap';
  
  if (variant === 'critical') {
    return (
      <button
        onClick={onClick}
        className={`${baseClasses} ${
          active 
            ? 'bg-red-100 text-red-700' 
            : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
        }`}
      >
        {children}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${
        active 
          ? 'bg-blue-100 text-blue-700' 
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
};
